import { Modal } from '@querodelivery/uni-react';
import {
  ENUM_DELIVERY_TYPE,
  TRANSLATE_BILLING_TYPE,
  TRANSLATE_DELIVERY_TYPE,
} from '@/_features/deliveries/constants';
import { convertMetersToKilometers, formatAddress } from '@/Utils/FnUtils';
import JsonView from '@/Shared/JsonView';

import styles from './delivery-modal.module.scss';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';

import {
  selectSelectedDeliveryId,
  setSelectedDeliveryId,
} from '@/_store/modules/deliveries';
import { useGetDeliveryDetail } from '@/_features/deliveries/api/get-delivery-detail';
import { DeliveryStatus } from '@/_features/deliveries/componentes/delivery-status/delivery-status';
import type { StatusDelivery } from '@/Interfaces/Data';
import { DeliveryInformationBox } from '@/_features/deliveries/componentes/delivery-information-box/delivery-information-box';
import { DistributionRulesBox } from '@/_features/deliveries/componentes/distribution-rules-box/distribution-rules-box';
import { DeliveryActivityBox } from '@/_features/deliveries/componentes/delivery-activity-box/delivery-activity-box';
import { DeliverymanRouteBox } from '@/_features/deliveries/componentes/deliveryman-route-box/deliveryman-route-box';
import { hasDeliverymanRouteContent } from '@/_features/deliveries/componentes/deliveryman-route-box/utils';
import { DeliveryModalSkeleton } from './components/delivery-modal-skeleton/delivery-modal-skeleton';
import { MODAL_WIDTH } from '@/_constants/styles';

export function DeliveryModal() {
  const dispatch = useAppDispatch();

  const selectedDeliveryId = useAppSelector(selectSelectedDeliveryId);
  const { data: selectedDelivery } = useGetDeliveryDetail(
    { deliveryId: selectedDeliveryId },
    { enabled: !!selectedDeliveryId },
  );

  function closeModal() {
    dispatch(setSelectedDeliveryId(''));
  }

  const maxDistanceRadius = selectedDelivery?.fare?.maxDistaceRadius;
  const deliveryRadius = maxDistanceRadius
    ? convertMetersToKilometers(maxDistanceRadius).toLocaleString('pt-BR')
    : '- - -';

  const maxDistanceDropstoGroup =
    selectedDelivery?.fare?.maxDistanceDropsToGroup;
  const deliveryDistanceLimit = maxDistanceDropstoGroup
    ? convertMetersToKilometers(maxDistanceDropstoGroup).toLocaleString('pt-BR')
    : '- - -';

  const deliveryTypeLabel = selectedDelivery?.deliveryType
    ? TRANSLATE_DELIVERY_TYPE[selectedDelivery.deliveryType]
    : TRANSLATE_DELIVERY_TYPE.CLOUD;

  const billingTypeLabel = selectedDelivery?.billingType
    ? TRANSLATE_BILLING_TYPE[selectedDelivery.billingType]
    : selectedDelivery?.billingType;

  const formattedDeliveryTypeWithBilling =
    deliveryTypeLabel + (billingTypeLabel ? ` (${billingTypeLabel})` : '');

  const valueDeliveryman =
    selectedDelivery?.deliveryType === ENUM_DELIVERY_TYPE.CLOUD
      ? selectedDelivery.valorEntregador || 0
      : selectedDelivery?.valueToPayLinkedDeliveryman || 0;

  const pickupAddress = selectedDelivery?.enderecoOrigem
    ? formatAddress(selectedDelivery.enderecoOrigem)
    : '';

  const deliveryAddress = selectedDelivery?.enderecoDestino
    ? formatAddress(selectedDelivery.enderecoDestino)
    : '';

  const pickupAddressLabel =
    !pickupAddress || pickupAddress === 's/n'
      ? 'não disponível'
      : pickupAddress;
  const deliveryAddressLabel =
    !deliveryAddress || deliveryAddress === 's/n'
      ? 'não disponível'
      : deliveryAddress;

  const hasEvents =
    selectedDelivery?.eventos && selectedDelivery.eventos.length > 0;

  const showRouteBox =
    !!selectedDelivery && hasDeliverymanRouteContent(selectedDelivery);

  return (
    <Modal
      isOpen={!!selectedDeliveryId}
      onClose={closeModal}
      header={`${
        selectedDelivery ? `Entrega #${selectedDelivery.codigo}` : ''
      }`}
      width={MODAL_WIDTH.lg}
    >
      {!selectedDelivery ? (
        <DeliveryModalSkeleton />
      ) : (
        <div className={styles.container}>
          <DeliveryStatus
            deliveryStatus={selectedDelivery?.status as StatusDelivery}
            deliverymanName={selectedDelivery?.nomeEntregador}
            deliverymanPhone={selectedDelivery?.deliverymanPhone}
            deliverymanId={selectedDelivery?.entregadorId}
            onCloseModal={closeModal}
          />
          <div className={styles.informationsContainer}>
            <DeliveryInformationBox
              establishmentId={selectedDelivery?.estabelecimentoId}
              onCloseModal={closeModal}
              deliveryValue={selectedDelivery?.valorEntrega}
              totalOrderValue={selectedDelivery?.valorPedido}
              deliverymanFee={valueDeliveryman}
              deliveryOrigin={selectedDelivery?.originOrderType}
              change={selectedDelivery?.troco}
              distance={selectedDelivery?.rotaDistancia}
              deliveryType={formattedDeliveryTypeWithBilling}
              paymentMethod={selectedDelivery?.formaPagamento}
              establishmentName={selectedDelivery?.nomeEstabelecimento}
              pickupAddress={pickupAddressLabel}
            />
            <DistributionRulesBox
              deliveryRadius={deliveryRadius}
              maxDistanceBetweenDeliveries={deliveryDistanceLimit}
              maxDeliveriesPerDeliveryman={
                selectedDelivery.fare?.maxQuantityDeliveryGroup
              }
              dedicatedDeliveryman={
                selectedDelivery.fare?.hasDedicatedDeliveryman
              }
              dedicatedOrder={
                selectedDelivery.fare?.hasOnlyDeliverymenDedicated
              }
              customerName={selectedDelivery?.nomeDestinatario}
              proxy={selectedDelivery?.proxyCall}
              customerPhone={selectedDelivery?.telefoneDestinatario}
              deliveryAddress={deliveryAddressLabel}
              reference={selectedDelivery?.enderecoDestino?.referencia}
              observations={selectedDelivery?.notes}
            />
          </div>
          {showRouteBox && (
            <DeliverymanRouteBox
              delivery={selectedDelivery}
              placeAddress={pickupAddressLabel}
              deliveryAddress={deliveryAddressLabel}
            />
          )}
          {hasEvents && (
            <DeliveryActivityBox events={selectedDelivery.eventos} />
          )}
          <JsonView show name="Pedido" source={selectedDelivery} />
        </div>
      )}
    </Modal>
  );
}
